import { useSyncExternalStore } from "react";
import { appStore, setUi } from "../../app/store.ts";
import { BUILD } from "../../env.ts";
import { platform } from "../platform.ts";

/**
 * Keeps the game current without interrupting a level:
 *  - web/PWA: a new service worker waits; the city offers "Update" and the
 *    page reloads only when the player taps it (or on the next launch);
 *  - Android APK: every deploy publishes the web build as a live-update
 *    bundle. Installed apps look for it at launch, on resume, every half hour
 *    and from Settings, download it in the background and apply it on the
 *    next launch or when the player taps "Update"; a newer native shell is
 *    offered as an APK download.
 */
export interface AndroidRelease {
  readonly web: {
    readonly version: string;
    /** Build time (ms); older manifests lack it. */
    readonly builtAt?: number;
    readonly bundle: string;
    readonly sha256: string;
    readonly size: number;
    readonly minNativeBuild: number;
  };
  readonly apk: { readonly versionCode: number; readonly versionName: string; readonly url: string; readonly sha256: string; readonly size: number } | null;
}

/** What Settings shows about native updates. */
export interface NativeUpdateStatus {
  readonly phase: "idle" | "checking" | "downloading" | "done" | "failed";
  /** 0 to 1 while a bundle downloads. */
  readonly progress: number;
  /** Web version downloaded and waiting to be applied. */
  readonly bundle: string | null;
  /** Newer APK version name, when the native shell itself is outdated. */
  readonly apk: string | null;
}

const CHECK_EVERY_MS = 30 * 60_000;
let lastCheck = 0;
let waitingWorker: ServiceWorker | null = null;
let releaseCache: AndroidRelease | null = null;

// --- origin --------------------------------------------------------------------------

const ORIGIN_KEY = "nano.origin";

/**
 * Remembered so a live bundle built without VITE_PUBLIC_ORIGIN can never cut
 * an installed app off from its update channel.
 */
const origin = ((): string => {
  try {
    if (BUILD.publicOrigin) localStorage.setItem(ORIGIN_KEY, BUILD.publicOrigin);
    return BUILD.publicOrigin || (localStorage.getItem(ORIGIN_KEY) ?? "");
  } catch {
    return BUILD.publicOrigin;
  }
})();

/** Absolute origin of the deployed game (the APK has no same-origin server). */
export function publicOrigin(): string {
  return platform.native() ? origin : "";
}

export async function fetchRelease(): Promise<AndroidRelease | null> {
  try {
    const response = await fetch(`${publicOrigin()}/updates/android.json`, { cache: "no-store", credentials: "omit" });
    if (!response.ok) return null;
    releaseCache = (await response.json()) as AndroidRelease;
    return releaseCache;
  } catch {
    return null;
  }
}

export function absoluteUrl(path: string): string {
  return /^https?:/.test(path) ? path : `${publicOrigin() || (typeof location === "undefined" ? "" : location.origin)}${path}`;
}

// --- web / PWA ------------------------------------------------------------------------

function offerWeb(worker: ServiceWorker): void {
  waitingWorker = worker;
  setUi({ update: { kind: "web", version: "" } });
}

export function registerServiceWorker(): void {
  if (import.meta.env.DEV || platform.native() || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        if (registration.waiting && navigator.serviceWorker.controller) offerWeb(registration.waiting);
        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          installing?.addEventListener("statechange", () => {
            // Only an *update* (an old worker still controls the page) needs an offer.
            if (installing.state === "installed" && navigator.serviceWorker.controller) offerWeb(installing);
          });
        });
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible" && Date.now() - lastCheck > CHECK_EVERY_MS) {
            lastCheck = Date.now();
            void registration.update().catch(() => undefined);
          }
        });
        lastCheck = Date.now();
      })
      .catch((error: unknown) => console.warn("sw registration failed", error));
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading || !waitingWorker) return;
      reloading = true;
      window.location.reload();
    });
  });
}

// --- native (Capacitor) -----------------------------------------------------------------

let status: NativeUpdateStatus = { phase: "idle", progress: 0, bundle: null, apk: null };
const statusListeners = new Set<() => void>();

function setStatus(patch: Partial<NativeUpdateStatus>): void {
  status = { ...status, ...patch };
  for (const listener of statusListeners) listener();
}

export function useNativeUpdateStatus(): NativeUpdateStatus {
  return useSyncExternalStore(
    (listener) => {
      statusListeners.add(listener);
      return () => statusListeners.delete(listener);
    },
    () => status,
  );
}

async function nativeBuild(): Promise<number> {
  const { App } = await import("@capacitor/app");
  const info = await App.getInfo();
  return Number(info.build) || 0;
}

/** A bundle is news only if it was built after the one running (never a downgrade). */
function isNewer(web: AndroidRelease["web"]): boolean {
  if (web.version === BUILD.version) return false;
  return web.builtAt === undefined || web.builtAt > BUILD.builtAt;
}

/** Called once at start-up inside the APK: confirm the running bundle, then look for news. */
export async function startNativeUpdates(): Promise<void> {
  // The Google Play app updates through Play only (Play policy): no bundles, no APK offers.
  if (!platform.native() || BUILD.store === "play") return;
  const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
  // Tells the plugin this bundle booted fine (otherwise it rolls back after readyTimeout).
  await LiveUpdate.ready().catch(() => undefined);
  await LiveUpdate.addListener("downloadBundleProgress", (event) => {
    if (status.phase === "downloading") setStatus({ progress: event.progress });
  }).catch(() => undefined);
  const due = () => document.visibilityState === "visible" && Date.now() - lastCheck > CHECK_EVERY_MS;
  document.addEventListener("visibilitychange", () => {
    if (due()) void checkNativeUpdate();
  });
  window.addEventListener("online", () => {
    if (status.phase === "failed" || due()) void checkNativeUpdate();
  });
  setInterval(() => {
    if (due()) void checkNativeUpdate();
  }, CHECK_EVERY_MS);
  await checkNativeUpdate();
}

let running: Promise<NativeUpdateStatus> | null = null;

/** Looks for a newer web bundle (downloaded right away) and a newer APK; one check at a time. */
export function checkNativeUpdate(): Promise<NativeUpdateStatus> {
  if (!platform.native() || BUILD.store === "play") return Promise.resolve(status);
  running ??= runNativeCheck().finally(() => {
    running = null;
  });
  return running;
}

async function runNativeCheck(): Promise<NativeUpdateStatus> {
  lastCheck = Date.now();
  setStatus({ phase: "checking", progress: 0 });
  const release = await fetchRelease();
  if (!release) {
    setStatus({ phase: "failed" });
    return status;
  }
  try {
    const build = await nativeBuild();
    const apk = release.apk && release.apk.versionCode > build ? release.apk.versionName : null;
    if (isNewer(release.web) && build >= release.web.minNativeBuild) {
      const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
      const { bundleIds } = await LiveUpdate.getBundles();
      if (!bundleIds.includes(release.web.version)) {
        setStatus({ phase: "downloading", progress: 0 });
        await LiveUpdate.downloadBundle({
          url: absoluteUrl(release.web.bundle),
          bundleId: release.web.version,
          checksum: release.web.sha256,
        });
      }
      // Applied automatically on the next launch, or now if the player taps "Update".
      await LiveUpdate.setNextBundle({ bundleId: release.web.version });
      setStatus({ bundle: release.web.version });
    }
    setStatus({ phase: "done", apk });
    // The city banner: a new shell matters more than a bundle that applies itself anyway.
    if (apk) setUi({ update: { kind: "apk", version: apk } });
    else if (status.bundle) setUi({ update: { kind: "bundle", version: status.bundle } });
  } catch (error) {
    console.warn("update check failed", error);
    setStatus({ phase: "failed" });
  }
  return status;
}

// --- applying -------------------------------------------------------------------------

/** Never mid-level: the banner and Settings that call this only show in the city. */
export async function applyUpdate(): Promise<void> {
  const offer = appStore.get().ui.update;
  if (!offer || appStore.get().ui.screen === "puzzle") return;
  if (offer.kind === "web") {
    if (waitingWorker) waitingWorker.postMessage({ type: "SKIP_WAITING" });
    else window.location.reload();
    return;
  }
  if (offer.kind === "bundle") {
    await applyNativeBundle();
    return;
  }
  await downloadApk();
}

/** Restarts the WebView on the downloaded bundle. */
export async function applyNativeBundle(): Promise<void> {
  if (!status.bundle || appStore.get().ui.screen === "puzzle") return;
  const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
  await LiveUpdate.reload();
}

/** Opens the newest APK in the system browser, which downloads and installs it. */
export async function downloadApk(): Promise<void> {
  const release = releaseCache ?? (await fetchRelease());
  if (release?.apk) await openExternal(absoluteUrl(release.apk.url));
}

export function dismissUpdate(): void {
  setUi({ update: null });
}

/** Opens a URL outside the WebView (APK downloads go through the system browser). */
export async function openExternal(url: string): Promise<void> {
  if (platform.native()) {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url });
  } else {
    window.location.href = url;
  }
}
